import { useEffect, useCallback } from 'react';
import { useHotelHub } from '../../providers/signalr/HotelHubProvider';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useHotel } from '../../contexts/HotelContext';
import { useTranslation } from 'react-i18next';
import { useBrowserNotifications } from '../useBrowserNotifications';
import { logger } from '../../utils/logger';

export interface NegativeSentimentAlertData {
  qrTokenId: string;
  roomToken?: string;
  roomNumber: string;
  messagePreview: string;
  negativityScore: number;
}

interface UseSentimentAlertsOptions {
  onNegativeSentimentDetected?: (data: NegativeSentimentAlertData) => void;
}

export const useSentimentAlerts = (options?: UseSentimentAlertsOptions) => {
  const { on, off, isConnected } = useHotelHub();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { myHotelInfo } = useHotel();
  const { showNotification, permission: notificationPermission } = useBrowserNotifications();

  // Kept for diagnostics/UI, but server-side gating is authoritative.
  const isFeatureEnabled = myHotelInfo?.isSentimentAnalysisEnabled ?? false;

  // Debug logging
  logger.log('[useSentimentAlerts] State:', {
    isConnected,
    isFeatureEnabled,
    isSentimentAnalysisEnabled: myHotelInfo?.isSentimentAnalysisEnabled,
    hotelId: myHotelInfo?.hotelId,
  });

  const handleNegativeSentimentAlert = useCallback((...args: unknown[]) => {
    // Backward-compatible parser:
    // Legacy payload: (qrTokenId, roomNumber, messagePreview, negativityScore)
    // New payload:    (qrTokenId, roomToken, roomNumber, messagePreview, negativityScore)
    const qrTokenId = String(args[0] ?? '');
    if (!qrTokenId) return;

    let roomToken: string | undefined;
    let roomNumber = '';
    let messagePreview = '';
    let negativityScore = 0;

    if (args.length >= 5) {
      roomToken = String(args[1] ?? '');
      roomNumber = String(args[2] ?? '');
      messagePreview = String(args[3] ?? '');
      negativityScore = Number(args[4] ?? 0);
    } else {
      roomNumber = String(args[1] ?? '');
      messagePreview = String(args[2] ?? '');
      negativityScore = Number(args[3] ?? 0);
    }

    const targetRoom = roomToken || qrTokenId;

    logger.log('[useSentimentAlerts] Received alert:', {
      qrTokenId,
      roomToken,
      roomNumber,
      messagePreview,
      negativityScore,
      targetRoom,
    });

    // Show warning toast with click handler to navigate to chat
    const truncatedPreview = messagePreview.length > 80
      ? messagePreview.substring(0, 80) + '...'
      : messagePreview;

    toast.warning(
      <div
        className="cursor-pointer"
        onClick={() => navigate(`/admin/chat?room=${encodeURIComponent(targetRoom)}`)}
      >
        <div className="font-semibold">
          {t('adminChatPage.negativeSentimentAlert', 'Negative Feedback Detected')} - {t('adminChatPage.roomLabel', 'Room')} {roomNumber}
        </div>
        <div className="text-sm opacity-90 mt-1">{truncatedPreview}</div>
        <div className="text-xs opacity-75 mt-1">
          {t('adminChatPage.negativityScore', { score: Math.round(negativityScore * 100), defaultValue: `Negativity: ${Math.round(negativityScore * 100)}%` })}
        </div>
      </div>,
      {
        autoClose: 10000, // 10 seconds
        closeOnClick: false,
      }
    );

    // Show browser notification (only when tab is not focused)
    showNotification(
      t('adminChatPage.negativeSentimentAlert', 'Negative Feedback Detected'),
      {
        body: `${t('adminChatPage.roomLabel', 'Room')} ${roomNumber}: ${truncatedPreview}`,
        tag: `sentiment-${targetRoom}`, // Group by room/session
        requireInteraction: true, // Keep notification visible until user interacts
        onclick: function() {
          window.focus();
          navigate(`/admin/chat?room=${encodeURIComponent(targetRoom)}`);
        },
      }
    );

    // Call the callback if provided (for updating chat state with sentiment)
    if (options?.onNegativeSentimentDetected) {
        options.onNegativeSentimentDetected({
          qrTokenId,
          roomToken,
          roomNumber,
          messagePreview,
          negativityScore,
        });
      }
  }, [navigate, t, showNotification, options]);

  useEffect(() => {
    if (!isConnected) {
      logger.log('[useSentimentAlerts] Not subscribing: connection not ready');
      return;
    }

    logger.log('[useSentimentAlerts] Subscribing to ReceiveNegativeSentimentAlert');
    on('ReceiveNegativeSentimentAlert', handleNegativeSentimentAlert);

    return () => {
      logger.log('[useSentimentAlerts] Unsubscribing from ReceiveNegativeSentimentAlert');
      off('ReceiveNegativeSentimentAlert', handleNegativeSentimentAlert);
    };
  }, [isConnected, isFeatureEnabled, on, off, handleNegativeSentimentAlert]);

  return { isConnected, isFeatureEnabled, notificationPermission };
};
